import type { i18n as I18n } from 'i18next';
import { FileSearchIcon, UsersRoundIcon } from 'lucide-react';
import { useMemo } from 'react';

import { useModuleRegistry } from '@/shared/registry/context';
import type { IconComponent } from '@/shared/registry/types';

/** Backend modules whose screens live in a frontend module with another id. */
const FRONTEND_MODULE: Readonly<Record<string, string>> = { identity: 'company' };

/** The frontend module (registry id) that owns a backend module's entities: `identity` → `company`. */
export const frontendModuleOf = (module: string): string => FRONTEND_MODULE[module] ?? module;

/** The backend module of a frontend module (registry id): `company` → `identity`. */
const backendModuleOf = (id: string): string =>
  Object.entries(FRONTEND_MODULE).find(([, frontend]) => frontend === id)?.[0] ?? id;

/**
 * Modules shown as their own groups in the ⌘K palette, in this order; hits of other modules share one group.
 * Employees (identity) are not here: the palette lists people from the directory.
 */
export const PALETTE_MODULES = ['tasks', 'feed', 'messenger', 'calendar', 'disk', 'wiki', 'groups'] as const;

/**
 * Modules left out of the palette's live results (`exclude`): employees (the palette lists people from the directory)
 * and every module that lists its own entities in the palette — a command source whose id is the module id, e.g. the
 * groups module's "groups" — so the same entity never shows up twice. Their hits stay on the results page.
 */
export function usePaletteExclusions(): readonly string[] {
  const registry = useModuleRegistry();
  return useMemo(() => {
    const own = registry.modules
      .filter((module) => module.commands?.some((source) => source.id === module.id))
      .map((module) => backendModuleOf(module.id));
    return [...new Set(['identity', ...own])].sort();
  }, [registry]);
}

/** Display name of a module in search: this module's label, else the module's navigation title. */
export function moduleLabel(module: string, i18n: Pick<I18n, 't' | 'exists'>): string {
  const own = `search:modules.${module}`;
  if (i18n.exists(own)) return i18n.t(own);
  const nav = `${frontendModuleOf(module)}:nav.title`;
  return i18n.exists(nav) ? i18n.t(nav) : module;
}

/** Icon of a module's results: its navigation icon (people for employees). */
export function useResultIcon(module: string): IconComponent {
  const registry = useModuleRegistry();
  if (module === 'identity') return UsersRoundIcon;
  const id = frontendModuleOf(module);
  return registry.navItems.find((item) => item.id === id)?.icon ?? FileSearchIcon;
}
